import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaIndustry,
  FaStore,
  FaUserTie,
  FaLayerGroup,
  FaCheckCircle,
  FaPauseCircle,
} from "react-icons/fa";
import { ListSkeleton, EmptyState, ListView, ListToolbar } from "../../shared";
import FirmCard from "../FirmCard";
import { buildFirmTree, flattenFirmTree } from "../../../utils/firmTree";
import { Pagination } from "../../atoms";
import { getFirmTypeKey } from "../../../utils/firmTypeStyles";

const FILTERS = [
  { id: "all", label: "Tümü", icon: FaBuilding },
  { id: "storage", label: "Depo", icon: FaIndustry },
  { id: "dealer", label: "Bayi", icon: FaStore },
  { id: "customer", label: "Müşteri", icon: FaUserTie },
];

// Durum filtresi (#445) — istemcide; ilk seçenek varsayılan
const STATUS_FILTERS = [
  { id: "all", label: "Tüm Durumlar", icon: FaLayerGroup },
  { id: "active", label: "Aktif", icon: FaCheckCircle },
  { id: "passive", label: "Pasif", icon: FaPauseCircle },
];

const TABLE_HEADERS = [
  // Ana Firma / Bağlı Bayiler sütunları yok (#215): hiyerarşi ağaçta görünür.
  { label: "FİRMA BİLGİLERİ", width: "w-[40%]", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "KOTALAR", width: "w-[240px] max-w-[240px]", skeletonWidth: "w-20", skeletonExtra: true },
  // Cihaz adedi (#445) — makine ikonu + sayı
  { label: "CİHAZ", width: "w-[110px]", skeletonWidth: "w-10" },
  { label: "İLETİŞİM", width: "w-[30%]", skeletonWidth: "w-24", skeletonExtra: true },
];

function FirmsList({
  firmsData = [],
  isLoading,
  canNavigate = false,
  searchParams = {},
  onSearchParamsChange = {},
}) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasTyped, setHasTyped] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const hasCalculatedLimitRef = useRef(false);
  const contentRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasCalculatedLimitRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      if (hasCalculatedLimitRef.current) return;
      const { height } = entries[0]?.contentRect || {};
      if (height && height > 80) {
        const rowHeight = 56;
        const count = Math.max(5, Math.floor(height / rowHeight));
        setItemsPerPage(count);
        hasCalculatedLimitRef.current = true;
        ro.disconnect();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const selectedDeviceId = location.state?.selectedDeviceId;
    if (selectedDeviceId && firmsData.length > 0 && canNavigate) {
      const targetFirm = firmsData.find((firm) =>
        firm.devices?.some((d) => d === selectedDeviceId)
      );
      if (targetFirm) {
        navigate(`/Anasayfa/Firma/${targetFirm._id}`, {
          state: { firm: targetFirm, fromPage: "Firma" },
        });
      }
    }
  }, [location.state, firmsData, canNavigate, navigate]);

  const statusFilter = searchParams?.status || "all";
  const filteredFirms = useMemo(() => {
    return firmsData.filter((firm) => {
      if (!firm) return false;
      if (statusFilter === "active" && !firm.isActive) return false;
      if (statusFilter === "passive" && firm.isActive) return false;
      if (activeFilter === "storage" && firm.firmType !== "storage") return false;
      if (activeFilter === "dealer" && firm.firmType !== "dealer") return false;
      if (activeFilter === "customer" && (firm.firmType === "storage" || firm.firmType === "dealer")) return false;
      return true;
    });
  }, [firmsData, activeFilter, statusFilter]);

  // AĞAÇ (#215): bayi → ana firma → şube. Arama ya da tip filtresi varken
  // bulunan firmalar üst zinciriyle (soluk satırlar) AÇIK gelir; yoksa yalnız
  // kökler listelenir, ▸ ile açılır. Sayfalama KÖK satırlara göredir.
  const isFiltered =
    activeFilter !== "all" ||
    statusFilter !== "all" ||
    ["firmName", "bayserNo", "mainFirmName", "dealerName"].some((k) =>
      String(searchParams?.[k] || "").trim()
    );
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const treeRoots = useMemo(() => buildFirmTree(filteredFirms), [filteredFirms]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const pageRoots = treeRoots.slice(startIndex, startIndex + itemsPerPage);
    return flattenFirmTree(pageRoots, (id) => isFiltered || expandedIds.has(id));
  }, [treeRoots, currentPage, itemsPerPage, isFiltered, expandedIds]);

  const totalPages = Math.ceil(treeRoots.length / itemsPerPage);
  const prevTotalPagesRef = useRef(totalPages);
  useEffect(() => {
    if (totalPages > 0) prevTotalPagesRef.current = totalPages;
  }, [totalPages]);
  const displayTotalPages = totalPages > 0 ? totalPages : prevTotalPagesRef.current;

  useEffect(() => { setCurrentPage(1); }, [activeFilter, searchParams]);

  useEffect(() => {
    if (searchParams?.firmType && searchParams.firmType !== activeFilter) {
      setActiveFilter(searchParams.firmType);
    }
    if (!searchParams?.firmType && activeFilter !== "all") {
      setActiveFilter("all");
    }
  }, [searchParams?.firmType, activeFilter]);

  const treeProps = (row) => ({
    depth: row.depth,
    hasChildren: row.hasChildren,
    // Filtrede ağaç hep açık — ok gizlenmez ama kapatma yerine bilgi verir.
    expanded: row.expanded,
    descendantCount: row.node.descendantCount,
    onToggle: isFiltered ? undefined : () => toggleExpanded(row.node.id),
    // Açık grup (#215): kök ve alt firmaları kökün firma tipi renginde tonlanır,
    // grup diğer satırlardan ayrışır. Kök "head", altındakiler "body".
    group: row.groupOpen
      ? { typeKey: getFirmTypeKey(row.root.firm), role: row.depth === 0 ? "head" : "body" }
      : null,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  };

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setHasTyped(true);
    onSearchParamsChange.setFirmType?.(filterId);
  };

  const handleSearch = (setter) => (e) => {
    setHasTyped(true);
    setter?.(e.target.value);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="bg-white flex-1 min-h-0 flex flex-col">
        {/* Filtre ve Arama */}
        <ListToolbar
          className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
          mobileFilterSlotId="firms-toolbar-mobile-slot"
          filterTabs={{
            filters: FILTERS,
            activeFilter,
            onChange: handleFilterChange,
            totalCount: filteredFirms.length,
            isLoading,
            color: "blue",
          }}
          secondaryFilterTabs={{
            filters: STATUS_FILTERS,
            activeFilter: statusFilter,
            onChange: (id) => {
              setHasTyped(true);
              onSearchParamsChange.setStatus?.(id);
            },
            isLoading,
            color: "blue",
          }}
          searchFields={[
            {
              placeholder: "Firma adı",
              value: searchParams.firmName || "",
              onChange: handleSearch(onSearchParamsChange.setFirmName),
            },
            {
              placeholder: "Bayser no",
              value: searchParams.bayserNo || "",
              onChange: handleSearch(onSearchParamsChange.setBayserNo),
            },
            {
              placeholder: "Ana firma adı",
              value: searchParams.mainFirmName || "",
              onChange: handleSearch(onSearchParamsChange.setMainFirmName),
              hideOnMobile: true,
            },
            {
              placeholder: "Bayi ismi",
              value: searchParams.dealerName || "",
              onChange: handleSearch(onSearchParamsChange.setDealerName),
              hideOnMobile: true,
            },
          ]}
          searchNote={
            hasTyped && isLoading ? (
              <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-600">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>Aranıyor...</span>
              </div>
            ) : null
          }
        />

        {/* İçerik */}
        <ListView
          contentRef={contentRef}
          headers={TABLE_HEADERS.map((h) => ({ label: h.label, className: h.width }))}
          tableClassName="table-fixed"
          data={paginatedRows}
          rowKey={(row, i) => row.node?.id || i}
          renderRow={(row) => (
            <FirmCard
              firm={row.node.firm}
              canNavigate={canNavigate}
              hideRelationColumns
              showDeviceCount
              showContacts
              showOwnerInTag
              dimPassive
              stub={!row.node.matched}
              tree={treeProps(row)}
            />
          )}
          renderMobileCard={(row) => (
            <FirmCard
              firm={row.node.firm}
              canNavigate={canNavigate}
              isMobileView={true}
              hideRelationColumns
              showDeviceCount
              showContacts
              showOwnerInTag
              dimPassive
              stub={!row.node.matched}
              tree={treeProps(row)}
            />
          )}
          isLoading={isLoading}
          loadingNode={
            <ListSkeleton columns={TABLE_HEADERS} rowCount={20} mobileCardCount={6} />
          }
          empty={
            <div className="p-4 md:p-6">
              <EmptyState message="Firma bulunamadı" />
            </div>
          }
          footer={
            filteredFirms.length > 0 || isLoading ? (
              <div className="shrink-0 border-t border-gray-200 bg-white">
                <Pagination
                  currentPage={currentPage}
                  totalPages={isLoading && totalPages === 0 ? displayTotalPages : totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  totalItems={treeRoots.length}
                  showInfo={true}
                  disabled={isLoading}
                />
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}

export default FirmsList;
